import {
  Fragment,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from "react";

import { cx, styled } from "simplydo/react/styled";

import { StyledAccordion } from "./Accordion.styles";
import type {
  AccordionActiveIndex,
  AccordionContentProps,
  AccordionPanelShorthand,
  AccordionProps,
  AccordionTitleProps,
} from "./Accordion.types";
import { AccordionContent } from "./AccordionContent";
import { AccordionContext } from "./AccordionContext";
import { AccordionTitle } from "./AccordionTitle";

function toNumericIndex(itemIndex: number | string): number {
  return typeof itemIndex === "number" ? itemIndex : Number(itemIndex);
}

function isIndexActive(
  exclusive: boolean,
  activeIndex: AccordionActiveIndex,
  itemIndex: number | string | undefined,
): boolean {
  if (itemIndex === undefined) {
    return false;
  }
  const numeric = toNumericIndex(itemIndex);
  if (Number.isNaN(numeric)) {
    return false;
  }
  if (exclusive) {
    return activeIndex === numeric;
  }
  return Array.isArray(activeIndex) && activeIndex.includes(numeric);
}

function computeNewIndex(
  exclusive: boolean,
  activeIndex: AccordionActiveIndex,
  itemIndex: number | string,
): AccordionActiveIndex {
  const numeric = toNumericIndex(itemIndex);
  if (Number.isNaN(numeric)) {
    return activeIndex;
  }

  if (exclusive) {
    return numeric === activeIndex ? -1 : numeric;
  }

  const current = Array.isArray(activeIndex) ? activeIndex : [];
  if (current.includes(numeric)) {
    return current.filter((i) => i !== numeric);
  }
  return [...current, numeric];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !isValidElement(value)
  );
}

function resolveTitle(
  title: AccordionPanelShorthand["title"],
  defaultProps: AccordionTitleProps,
): ReactNode {
  // Click handling goes through AccordionContext (Title calls handleTitleClick).
  // Do not also pass handleTitleClick as onClick — that would double-toggle.
  if (title == null || typeof title === "boolean") {
    return null;
  }

  if (typeof title === "function") {
    return title(AccordionTitle, { ...defaultProps });
  }

  if (isValidElement(title)) {
    return title;
  }

  if (typeof title === "string" || typeof title === "number") {
    return <AccordionTitle {...defaultProps}>{title}</AccordionTitle>;
  }

  if (isPlainObject(title)) {
    return (
      <AccordionTitle {...defaultProps} {...(title as AccordionTitleProps)} />
    );
  }

  return (
    <AccordionTitle {...defaultProps}>{title as ReactNode}</AccordionTitle>
  );
}

function resolveContent(
  content: AccordionPanelShorthand["content"],
  defaultProps: AccordionContentProps,
): ReactNode {
  if (content == null || typeof content === "boolean") {
    return null;
  }

  if (typeof content === "function") {
    // Semantic shorthand: (Component, props) => ReactNode
    return content(AccordionContent, defaultProps);
  }

  if (isValidElement(content)) {
    return content;
  }

  if (typeof content === "string" || typeof content === "number") {
    return <AccordionContent {...defaultProps}>{content}</AccordionContent>;
  }

  if (isPlainObject(content)) {
    return (
      <AccordionContent
        {...defaultProps}
        {...(content as AccordionContentProps)}
      />
    );
  }

  return (
    <AccordionContent {...defaultProps}>
      {content as ReactNode}
    </AccordionContent>
  );
}

const AccordionComponent = (
  props: AccordionProps & { ref?: Ref<HTMLDivElement> },
): ReactElement => {
  const exclusive = props.exclusive ?? true;
  const {
    ref,
    styled: isStyled,
    fluid,
    className,
    style,
    children,
    activeIndex: activeIndexProp,
    defaultActiveIndex,
    panels,
    onTitleClick,
  } = props;

  const isControlled = activeIndexProp !== undefined;
  const [uncontrolledIndex, setUncontrolledIndex] =
    useState<AccordionActiveIndex>(() => {
      if (defaultActiveIndex !== undefined) {
        return defaultActiveIndex;
      }
      return exclusive ? -1 : [];
    });

  const activeIndex = isControlled ? activeIndexProp : uncontrolledIndex;

  const setActiveIndex = useCallback(
    (next: AccordionActiveIndex) => {
      if (!isControlled) {
        setUncontrolledIndex(next);
      }
    },
    [isControlled],
  );

  const handleTitleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, titleProps: AccordionTitleProps) => {
      const { index } = titleProps;
      if (index === undefined) {
        onTitleClick?.(event, titleProps);
        return;
      }
      setActiveIndex(computeNewIndex(exclusive, activeIndex, index));
      onTitleClick?.(event, titleProps);
    },
    [activeIndex, exclusive, onTitleClick, setActiveIndex],
  );

  const isActive = useCallback(
    (index: number | string | undefined) =>
      isIndexActive(exclusive, activeIndex, index),
    [activeIndex, exclusive],
  );

  const contextValue = useMemo(
    () => ({
      exclusive,
      activeIndex,
      isIndexActive: isActive,
      handleTitleClick,
    }),
    [activeIndex, exclusive, handleTitleClick, isActive],
  );

  const panelNodes =
    children == null && panels
      ? panels.map((panel, index) => {
          const key = panel.key ?? index;
          const active = isActive(index);
          const titleNode = resolveTitle(panel.title, { active, index });
          const contentNode = resolveContent(panel.content, { active });
          return (
            <Fragment key={key}>
              {titleNode}
              {contentNode}
            </Fragment>
          );
        })
      : null;

  return (
    <AccordionContext.Provider value={contextValue}>
      <StyledAccordion
        ref={ref}
        className={cx(
          className,
          isStyled ? "is-styled" : "",
          fluid ? "is-fluid" : "",
        )}
        style={style}
      >
        {children != null ? children : panelNodes}
      </StyledAccordion>
    </AccordionContext.Provider>
  );
};

export const Accordion = styled(AccordionComponent)``;
